import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AnnouncementDto } from '../../core/models/announcement.models';
import { FeedbackType } from '../../core/models/feedback.models';
import { AnnouncementService } from '../../core/services/announcement.service';
import { AuthService } from '../../core/services/auth.service';
import { FeedbackService } from '../../core/services/feedback.service';
import { LanguageService } from '../../core/services/language.service';
import { LayoutService } from '../../core/services/layout.service';
import { ThemeService } from '../../core/services/theme.service';
import { ApiCacheService } from '../../core/cache/api-cache.service';
import {
  Bell,
  Bookmark,
  Bug,
  ChevronDown,
  Gift,
  Heart,
  Info,
  Languages,
  Lightbulb,
  LogOut,
  LucideAngularModule,
  LucideIconData,
  Menu,
  Megaphone,
  MessageSquarePlus,
  Moon,
  Newspaper,
  Send,
  Sun,
  Trash2,
  TriangleAlert,
  User,
  X,
} from 'lucide-angular';

const READ_KEY = 'bepos_read_ann';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, RouterModule, TranslatePipe],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly authService = inject(AuthService);
  private readonly announcementService = inject(AnnouncementService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly apiCache = inject(ApiCacheService);
  private readonly destroyRef = inject(DestroyRef);
  readonly themeService = inject(ThemeService);
  readonly languageService = inject(LanguageService);
  currentUser = this.authService.currentUser;
  panelOpen = signal(false);
  userMenuOpen = signal(false);
  announcements = signal<AnnouncementDto[]>([]);

  /**
   * "Keshni tozalash" tugmasi ikki bosqichli: birinchi bosishda tasdiq so'raydi.
   *
   * Amal sahifani yangilaydi, sarlavha esa savdo ekranida ham ko'rinib turadi —
   * bitta tasodifiy bosish yig'ilayotgan chekni yo'q qilib yuborardi.
   */
  cacheClearArmed = signal(false);
  cacheClearing = signal(false);

  // Feedback dialog
  feedbackOpen = signal(false);
  feedbackType = signal<FeedbackType>('Suggestion');
  feedbackMessage = signal('');
  feedbackSubmitting = signal(false);
  feedbackSent = signal(false);

  readonly feedbackTypes: {
    type: FeedbackType;
    label: string;
    icon: LucideIconData;
    color: string;
  }[] = [
    {
      type: 'Bug',
      label: 'FEEDBACK.TYPES.BUG',
      icon: Bug,
      color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    },
    {
      type: 'Suggestion',
      label: 'FEEDBACK.TYPES.SUGGESTION',
      icon: Lightbulb,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    {
      type: 'Praise',
      label: 'FEEDBACK.TYPES.PRAISE',
      icon: Heart,
      color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    },
  ];

  readonly languages = [
    { code: 'uz', label: "O'zbekcha" },
    { code: 'uz-cyrl', label: 'Ўзбекча' },
    { code: 'ru', label: 'Русский' },
    { code: 'en', label: 'English' },
  ];

  private readIds = signal<Set<string>>(this.loadReadIds());

  unreadCount = computed(() => {
    const read = this.readIds();
    return this.announcements().filter((a) => !read.has(a.id)).length;
  });

  userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return 'AD';
    return user.fullName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  });

  // Icons
  Menu = Menu;
  Bell = Bell;
  LogOut = LogOut;
  ChevronDown = ChevronDown;
  X = X;
  Info = Info;
  Gift = Gift;
  TriangleAlert = TriangleAlert;
  Newspaper = Newspaper;
  Megaphone = Megaphone;
  Bookmark = Bookmark;
  User = User;
  Languages = Languages;
  Sun = Sun;
  Moon = Moon;
  MessageSquarePlus = MessageSquarePlus;
  Send = Send;
  Bug = Bug;
  Lightbulb = Lightbulb;
  Heart = Heart;
  Trash2 = Trash2;

  ngOnInit() {
    this.announcementService
      .getAnnouncements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.announcements.set(res.data ?? []) });
  }

  togglePanel(event: Event) {
    event.stopPropagation();
    this.userMenuOpen.set(false);
    this.panelOpen.update((v) => !v);
    if (this.panelOpen()) this.markAllRead();
  }

  closePanel() {
    this.panelOpen.set(false);
  }

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    this.panelOpen.set(false);
    this.cacheClearArmed.set(false);
    this.userMenuOpen.update((v) => !v);
  }

  closeUserMenu() {
    this.userMenuOpen.set(false);
    this.cacheClearArmed.set(false);
  }

  /**
   * Keshni tozalab, sahifani yangilaydi.
   *
   * <p>
   * Bu — qo'llab-quvvatlash uchun oxirgi chora. Kesh o'zi to'g'ri yangilanishi
   * kerak (yozuv amallari uni bekor qiladi), lekin biror holat e'tibordan chetda
   * qolsa, foydalanuvchi bizga qo'ng'iroq qilmasdan o'zi chiqib ketishi mumkin
   * bo'lsin.
   * </p>
   *
   * <p>
   * Tozalash KUTILADI: `location.reload()` darhol chaqirilsa, IndexedDB
   * tranzaksiyasi uzilib, kesh joyida qolib ketardi — ya'ni tugma hech narsa
   * qilmagandek ko'rinardi.
   * </p>
   */
  async clearCache() {
    if (!this.cacheClearArmed()) {
      this.cacheClearArmed.set(true);
      return;
    }
    this.cacheClearing.set(true);
    await this.apiCache.clearAll();
    window.location.reload();
  }

  switchLanguage(lang: string) {
    this.languageService.switchLanguage(lang);
  }

  toggleDarkMode() {
    this.themeService.toggleDarkMode();
  }

  @HostListener('document:click')
  onDocClick() {
    this.panelOpen.set(false);
    this.closeUserMenu();
  }

  openFeedback(event: Event) {
    event.stopPropagation();
    this.panelOpen.set(false);
    this.userMenuOpen.set(false);
    this.feedbackMessage.set('');
    this.feedbackType.set('Suggestion');
    this.feedbackSent.set(false);
    this.feedbackOpen.set(true);
  }

  closeFeedback() {
    this.feedbackOpen.set(false);
  }

  submitFeedback() {
    const msg = this.feedbackMessage().trim();
    if (!msg || this.feedbackSubmitting()) return;
    this.feedbackSubmitting.set(true);
    this.feedbackService
      .submit({ message: msg, type: this.feedbackType() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.feedbackSubmitting.set(false);
          this.feedbackSent.set(true);
          this.feedbackMessage.set('');
        },
        error: () => this.feedbackSubmitting.set(false),
      });
  }

  markAllRead() {
    const ids = new Set(this.announcements().map((a) => a.id));
    this.readIds.set(ids);
    this.saveReadIds(ids);
  }

  isRead(id: string): boolean {
    return this.readIds().has(id);
  }

  iconFor(type: string) {
    switch (type) {
      case 'promo':
        return this.Gift;
      case 'warning':
        return this.TriangleAlert;
      case 'news':
        return this.Newspaper;
      default:
        return this.Info;
    }
  }

  colorFor(type: string): string {
    switch (type) {
      case 'promo':
        return 'text-purple-500 bg-purple-50 dark:bg-purple-900/30';
      case 'warning':
        return 'text-amber-500 bg-amber-50 dark:bg-amber-900/30';
      case 'news':
        return 'text-blue-500 bg-blue-50 dark:bg-blue-900/30';
      default:
        return 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/30';
    }
  }

  dotColorFor(type: string): string {
    switch (type) {
      case 'promo':
        return 'bg-purple-500';
      case 'warning':
        return 'bg-amber-500';
      case 'news':
        return 'bg-blue-500';
      default:
        return 'bg-emerald-500';
    }
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Hozir';
    if (m < 60) return `${m} daqiqa oldin`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} soat oldin`;
    const d = Math.floor(h / 24);
    return `${d} kun oldin`;
  }

  toggleSidebar() {
    this.layoutService.toggleSidebar();
  }

  handleLogout() {
    this.authService.logout();
  }

  private loadReadIds(): Set<string> {
    try {
      const raw = localStorage.getItem(READ_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  }

  private saveReadIds(ids: Set<string>) {
    try {
      localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
    } catch {
      /* ignore */
    }
  }
}
